import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { graphql } from '@eluve/graphql.tada';
import { Logger } from '@eluve/logger-main';
import { getMetadataFromFileName } from '@eluve/utils';

import { MarkClientAudioCompletedAtEvent, TaskHandler } from '../types';

const updateClientAudioCompletedAtMutation = graphql(`
  mutation updateClientAudioCompletedAt(
    $tenantId: uuid!
    $appointmentId: uuid!
    $fileId: uuid!
    $clientCompletedAt: timestamptz!
  ) {
    updateAppointmentFiles(
      where: {
        tenantId: { _eq: $tenantId }
        appointmentId: { _eq: $appointmentId }
        fileId: { _eq: $fileId }
        clientCompletedAt: { _isNull: true }
      }
      _set: { clientCompletedAt: $clientCompletedAt }
    ) {
      returning {
        __typename
        appointmentId
        fileId
        clientCompletedAt
      }
    }
  }
`);

const getAppointmentFileClientCompletedAt = graphql(`
  query getCompletedAt(
    $tenantId: uuid!
    $appointmentId: uuid!
    $fileId: uuid!
  ) {
    appointmentFilesByPk(
      tenantId: $tenantId
      appointmentId: $appointmentId
      fileId: $fileId
    ) {
      __typename
      appointmentId
      fileId
      clientCompletedAt
    }
  }
`);

export const markClientAudioCompletedAtHandler = ({
  apolloClient,
}: {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  logger: Logger;
}): TaskHandler<MarkClientAudioCompletedAtEvent> => {
  return async (
    { appointmentId, fileId, clientCompletedAt },
    { tenantId, userId, signal },
  ) => {
    const { databaseFileId } = getMetadataFromFileName({
      userId,
      fileName: fileId,
    });

    const result = await apolloClient.mutate({
      mutation: updateClientAudioCompletedAtMutation,
      variables: {
        tenantId,
        appointmentId,
        fileId: databaseFileId,
        clientCompletedAt,
      },
      context: { fetchOptions: { signal } },
    });

    if (!result.data?.updateAppointmentFiles?.returning?.length) {
      // Check to see if the record already has the clientCompletedAt set
      const existingRecord = await apolloClient.query({
        query: getAppointmentFileClientCompletedAt,
        variables: {
          tenantId,
          appointmentId,
          fileId: databaseFileId,
        },
        fetchPolicy: 'network-only',
        context: { fetchOptions: { signal } },
      });

      const record = existingRecord.data?.appointmentFilesByPk;

      // If the record doesn't exist, this could indicate a data integrity issue
      // Throw an error to trigger a retry
      if (!record) {
        throw new Error(
          'Appointment file record not found - possible data integrity issue',
        );
      }

      // If the record exists but clientCompletedAt is not set, the mutation failed
      // and we need to retry
      if (!record.clientCompletedAt) {
        throw new Error('Failed to mark client audio as completed');
      }
    }
  };
};
